import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormLabel,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import {
  getCommonStyles,
  getOnboardingCardSurfaceStyles,
  onboardingActionButtonSx,
} from 'components/onboarding/constants';
import { surfaceCardSx } from 'components/common/surfaces';
import ImageZoomModal from 'components/common/ImageZoomModal';
import { sendApiRequest, uploadFileRequest, readFile } from 'lib/api/public';
import notify from 'lib/notify';
import { IAttachment } from 'components/types';
import { formatUserDateTime } from 'components/utils';
import ScreenshotCapture from 'components/common/ScreenshotCapture';

type SupportTicket = {
  _id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  attachments?: IAttachment[];
  createdAt: string;
};

type View = 'list' | 'create' | 'detail';

type Props = {
  open: boolean;
  onClose: () => void;
};

const SupportPanel = ({ open, onClose }: Props) => {
  const theme = useTheme();
  const commonStyles = useMemo(() => getCommonStyles(theme), [theme]);
  const [isExpanded, setIsExpanded] = useState(false);

  const panelSx = useMemo(
    () => ({
      ...getOnboardingCardSurfaceStyles(theme),
      position: 'fixed' as const,
      bottom: { xs: '80px', sm: '90px' },
      width: isExpanded
        ? {
            xs: 'calc(100% - 32px)',
            sm: '560px',
            md: '640px',
          }
        : {
            xs: 'calc(100% - 32px)',
            sm: '360px',
            md: '400px',
          },
      maxWidth: '90vw',
      minHeight: isExpanded
        ? {
            xs: '560px',
            sm: '640px',
          }
        : {
            xs: '460px',
            sm: '520px',
          },
      height: isExpanded
        ? {
            xs: 'calc(100vh - 140px)',
            sm: '80vh',
          }
        : undefined,
      maxHeight: {
        xs: 'calc(100vh - 140px)',
        sm: '80vh',
      },
      display: 'flex',
      flexDirection: 'column' as const,
      zIndex: 1049,
      left: { xs: '16px', sm: 'auto' },
      right: { xs: 'auto', sm: '20px' },
      mx: { xs: 'auto', sm: 0 },
      transition: 'width 0.2s ease, min-height 0.2s ease, height 0.2s ease',
    }),
    [theme, isExpanded],
  );

  const [view, setView] = useState<View>('list');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<IAttachment[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
  const [screenshotOpen, setScreenshotOpen] = useState(false);
  const [zoomedImage, setZoomedImage] = useState<{ src: string; alt: string } | null>(null);

  const resetForm = () => {
    setSubject('');
    setDescription('');
    setAttachments([]);
  };

  const loadTickets = useCallback(async () => {
    setLoadingList(true);
    try {
      const response = await sendApiRequest<{ list: SupportTicket[] }>({
        path: '/support-tickets/list',
        method: 'GET',
      });
      setTickets(response.list || []);
    } catch (error) {
      notify(error);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setView('list');
      setSelectedTicket(null);
      resetForm();
      setScreenshotOpen(false);
      setZoomedImage(null);
      return;
    }
    loadTickets();
  }, [open, loadTickets]);

  const handleCreateClick = () => {
    resetForm();
    setView('create');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedTicket(null);
    setZoomedImage(null);
    resetForm();
  };

  const handleScreenshotCapture = async (screenshot: string) => {
    setScreenshotOpen(false);
    setIsUploadingScreenshot(true);
    try {
      const response = await fetch(screenshot);
      const blob = await response.blob();
      const fileName = `screenshot_${Date.now()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await uploadFileRequest({ formData });
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload screenshot');
      }

      const data = (await uploadResponse.json()) as { result?: string };
      if (!data.result) {
        throw new Error('Failed to upload screenshot');
      }

      setAttachments(prev => [
        ...prev,
        {
          name: fileName,
          url: data.result,
          date: new Date(),
        },
      ]);
      notify('Screenshot attached');
    } catch (error) {
      notify(error);
    } finally {
      setIsUploadingScreenshot(false);
    }
  };

  const removeAttachment = (attachment: IAttachment) => {
    setAttachments(prev => prev.filter(item => item.url !== attachment.url));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!subject.trim() || !description.trim()) {
      notify('Subject and description are required');
      return;
    }

    setIsSubmitting(true);
    try {
      await sendApiRequest({
        path: '/support-tickets/create',
        method: 'POST',
        data: {
          subject: subject.trim(),
          description: description.trim(),
          attachments: attachments.length ? attachments : undefined,
        },
      });
      notify('Support ticket submitted');
      resetForm();
      setView('list');
      await loadTickets();
    } catch (error) {
      notify(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  const title =
    view === 'create' ? 'Create Ticket' : view === 'detail' ? 'Ticket' : 'Support';
  const isCreateBusy = isUploadingScreenshot || isSubmitting;
  const createBusyLabel = isUploadingScreenshot
    ? 'Uploading screenshot…'
    : isSubmitting
      ? 'Submitting ticket…'
      : null;

  return (
    <>
      {!screenshotOpen && (
        <Paper elevation={8} sx={panelSx}>
          <Box
            sx={{
              ...commonStyles.gradientHeader,
              pb: { xs: 2, sm: 2.5 },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, pr: 1, gap: 0.5 }}>
                {view !== 'list' && (
                  <IconButton
                    size="small"
                    onClick={handleBackToList}
                    disabled={view === 'create' && isCreateBusy}
                    sx={{
                      color: 'inherit',
                      flexShrink: 0,
                      p: 0.25,
                      ml: -0.25,
                      minWidth: 28,
                      minHeight: 28,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: theme => alpha(theme.palette.common.white, 0.14),
                      },
                      '&.Mui-disabled': { color: 'inherit', opacity: 0.5 },
                    }}
                    aria-label="Back to ticket list"
                  >
                    <ChevronLeftIcon sx={{ fontSize: '1.25rem' }} />
                  </IconButton>
                )}
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, letterSpacing: '0.5px', fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {title}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                  <IconButton
                    size="small"
                    onClick={() => setIsExpanded(prev => !prev)}
                    sx={{
                      color: 'inherit',
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'scale(1.15)' },
                    }}
                    aria-label={isExpanded ? 'Collapse support panel' : 'Expand support panel'}
                  >
                    {isExpanded ? (
                      <CloseFullscreenIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                    ) : (
                      <OpenInFullIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                      color: 'inherit',
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'scale(1.2)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              bgcolor: theme =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.14)
                  : theme.palette.background.default,
              overflow: 'auto',
              flexGrow: 1,
              WebkitOverflowScrolling: 'touch',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {view === 'list' && (
              <>
                {loadingList ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                    <CircularProgress color="secondary" size={24} />
                  </Box>
                ) : tickets.length === 0 ? (
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      gap: 1.5,
                      px: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        border: 1,
                        borderColor: theme =>
                          theme.palette.mode === 'dark'
                            ? lighten(theme.palette.primary.main, 0.28)
                            : alpha(theme.palette.primary.main, 0.35),
                        boxShadow: theme =>
                          theme.palette.mode === 'dark'
                            ? `0 0 0 6px ${alpha(theme.palette.primary.main, 0.35)}`
                            : `0 0 0 6px ${alpha(theme.palette.primary.main, 0.12)}`,
                      }}
                    >
                      <SupportAgentIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        No support tickets yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Create a ticket and our team will follow up by email.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCreateClick}
                      sx={{ ...onboardingActionButtonSx, mt: 0.5 }}
                    >
                      Create Ticket
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        flexGrow: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      {tickets.map(ticket => (
                        <Box
                          key={ticket._id}
                          component="button"
                          type="button"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setView('detail');
                          }}
                          sx={{
                            ...surfaceCardSx,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            width: '100%',
                            textAlign: 'left',
                            cursor: 'pointer',
                            p: 1.5,
                            m: 0,
                            font: 'inherit',
                            color: 'inherit',
                            transition: 'border-color 0.15s, background-color 0.15s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: theme =>
                                theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.primary.main, 0.22)
                                  : alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 0.75,
                                py: 0.125,
                                mb: 0.75,
                                borderRadius: 1,
                                bgcolor: theme =>
                                  theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.common.white, 0.08)
                                    : alpha(theme.palette.primary.main, 0.08),
                                color: 'text.secondary',
                              }}
                            >
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: 0.2 }}
                              >
                                {ticket.ticketNumber}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                              {ticket.subject}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.25 }}
                            >
                              {formatUserDateTime(ticket.createdAt)}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.75,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {ticket.description.replace(/<[^>]*>/g, '')}
                            </Typography>
                          </Box>
                          <ChevronRightIcon
                            sx={{ color: 'text.secondary', fontSize: 20, mt: 0.25, flexShrink: 0 }}
                          />
                        </Box>
                      ))}
                    </Box>
                    <Box
                      sx={{
                        pt: 2,
                        mt: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        borderTop: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateClick}
                        sx={onboardingActionButtonSx}
                      >
                        Create Ticket
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}

            {view === 'detail' && selectedTicket && (
              <Box
                sx={{
                  ...surfaceCardSx,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  flexGrow: 1,
                  minHeight: 0,
                  overflow: 'auto',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 1,
                      bgcolor: theme =>
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.08)
                          : alpha(theme.palette.primary.main, 0.08),
                      color: 'text.secondary',
                    }}
                  >
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: 0.2 }}
                    >
                      {selectedTicket.ticketNumber}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatUserDateTime(selectedTicket.createdAt)}
                  </Typography>
                </Box>

                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.35 }}>
                  {selectedTicket.subject}
                </Typography>

                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: theme =>
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.04)
                        : 'background.default',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                    {selectedTicket.description.replace(/<[^>]*>/g, '')}
                  </Typography>
                </Box>

                {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.primary' }}
                    >
                      Attachments
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {selectedTicket.attachments.map(attachment => {
                        const href = readFile(attachment.url || '');
                        if (!href) {
                          return null;
                        }
                        const isImage = /\.(png|jpe?g|gif|webp)$/i.test(attachment.name || '');
                        const attachmentName = attachment.name || 'Attachment';

                        if (isImage) {
                          return (
                            <Box
                              key={attachment.url}
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                border: 1,
                                borderColor: 'divider',
                              }}
                            >
                              <Box
                                component="button"
                                type="button"
                                onClick={() => setZoomedImage({ src: href, alt: attachmentName })}
                                sx={{
                                  position: 'relative',
                                  display: 'block',
                                  width: '100%',
                                  border: 0,
                                  p: 0,
                                  m: 0,
                                  bgcolor: theme =>
                                    theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.common.black, 0.35)
                                      : 'background.default',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  cursor: 'zoom-in',
                                  '&:hover .zoom-overlay': { opacity: 1 },
                                }}
                              >
                                <Box
                                  component="img"
                                  src={href}
                                  alt={attachmentName}
                                  sx={{
                                    display: 'block',
                                    width: '100%',
                                    maxHeight: 180,
                                    objectFit: 'contain',
                                  }}
                                />
                                <Box
                                  className="zoom-overlay"
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    bgcolor: theme => alpha(theme.palette.common.black, 0.3),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                  }}
                                >
                                  <ZoomInIcon
                                    sx={{
                                      color: 'common.white',
                                      fontSize: '2rem',
                                      filter: theme =>
                                        `drop-shadow(0 2px 4px ${alpha(theme.palette.common.black, 0.5)})`,
                                    }}
                                  />
                                </Box>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  color: theme =>
                                    theme.palette.mode === 'dark'
                                      ? lighten(theme.palette.primary.main, 0.55)
                                      : 'primary.main',
                                  fontWeight: 500,
                                }}
                              >
                                {attachmentName}
                              </Typography>
                            </Box>
                          );
                        }

                        return (
                          <Box
                            key={attachment.url}
                            component="a"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'block',
                              p: 1,
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'divider',
                              textDecoration: 'none',
                              color: 'inherit',
                              transition: 'border-color 0.15s, background-color 0.15s',
                              '&:hover': {
                                borderColor: theme =>
                                  theme.palette.mode === 'dark'
                                    ? lighten(theme.palette.primary.main, 0.35)
                                    : 'primary.main',
                                bgcolor: theme =>
                                  theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.main, 0.18)
                                    : alpha(theme.palette.primary.main, 0.04),
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme =>
                                  theme.palette.mode === 'dark'
                                    ? lighten(theme.palette.primary.main, 0.55)
                                    : 'primary.main',
                                fontWeight: 500,
                              }}
                            >
                              {attachmentName}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {view === 'create' && (
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, gap: 0, position: 'relative' }}
              >
                {isCreateBusy && (
                  <Box sx={{ mb: 1.5 }}>
                    <LinearProgress
                      color="primary"
                      sx={{
                        height: 3,
                        borderRadius: 1,
                        mb: 1,
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: theme =>
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.2)
                            : alpha(theme.palette.primary.main, 0.06),
                      }}
                    >
                      <CircularProgress size={14} color="primary" />
                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                        {createBusyLabel}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    flexGrow: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    opacity: isCreateBusy ? 0.72 : 1,
                    pointerEvents: isCreateBusy ? 'none' : 'auto',
                    transition: 'opacity 0.15s',
                  }}
                >
                  <Box>
                    <FormLabel required sx={{ color: 'text.primary', fontWeight: 600, display: 'block', mb: 0.75 }}>
                      Subject
                    </FormLabel>
                    <TextField
                      value={subject}
                      onChange={event => setSubject(event.target.value)}
                      fullWidth
                      required
                      size="small"
                      disabled={isCreateBusy}
                      placeholder="Brief summary of the issue"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'background.paper',
                        },
                      }}
                    />
                  </Box>

                  <Box>
                    <FormLabel required sx={{ color: 'text.primary', fontWeight: 600, display: 'block', mb: 0.75 }}>
                      Description
                    </FormLabel>
                    <TextField
                      value={description}
                      onChange={event => setDescription(event.target.value)}
                      fullWidth
                      required
                      multiline
                      minRows={isExpanded ? 12 : 5}
                      size="small"
                      disabled={isCreateBusy}
                      placeholder="What happened? Steps to reproduce help a lot."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'background.paper',
                        },
                      }}
                    />
                  </Box>

                  {attachments.length > 0 && (
                    <Box>
                      <FormLabel sx={{ color: 'text.primary', fontWeight: 600, display: 'block', mb: 0.75 }}>
                        Attachments
                      </FormLabel>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.75,
                          p: 1,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        }}
                      >
                        {attachments.map(attachment => {
                          const href = readFile(attachment.url || '');
                          return (
                            <Box
                              key={attachment.url}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                              }}
                            >
                              {href ? (
                                <Typography
                                  component="a"
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="body2"
                                  sx={{
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  {attachment.name}
                                </Typography>
                              ) : (
                                <Typography variant="body2" noWrap>
                                  {attachment.name}
                                </Typography>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => removeAttachment(attachment)}
                                disabled={isCreateBusy}
                                aria-label="Remove attachment"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    pt: 2,
                    mt: 'auto',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                    gap: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Button
                    variant="contained"
                    color="inherit"
                    startIcon={
                      isUploadingScreenshot ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <CameraAltOutlinedIcon />
                      )
                    }
                    onClick={() => setScreenshotOpen(true)}
                    disabled={isCreateBusy}
                    sx={onboardingActionButtonSx}
                  >
                    {isUploadingScreenshot ? 'Uploading…' : 'Take a Screenshot'}
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isCreateBusy}
                    startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                    sx={onboardingActionButtonSx}
                  >
                    {isSubmitting ? 'Submitting…' : 'Submit'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {screenshotOpen && (
        <ScreenshotCapture
          onCapture={screenshot => {
            void handleScreenshotCapture(screenshot);
          }}
          onClose={() => setScreenshotOpen(false)}
        />
      )}

      <ImageZoomModal
        open={Boolean(zoomedImage)}
        onClose={() => setZoomedImage(null)}
        imageSrc={zoomedImage?.src || ''}
        alt={zoomedImage?.alt || ''}
      />
    </>
  );
};

export default SupportPanel;
